/** Audio settings as the player sets them: sliders are 0–100, persisted in `settingsStore`. */
export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
  /** Silence the game while its tab is hidden. */
  muteInBackground: boolean;
}

export const DEFAULT_AUDIO: AudioSettings = {
  master: 80,
  music: 50,
  sfx: 80,
  muted: false,
  muteInBackground: true,
};

/** A slider value as an integer 0..100, or `fallback` for anything that isn't a finite number. */
export function clampVolume(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v)
    ? Math.round(Math.min(100, Math.max(0, v)))
    : fallback;
}

/** Validates a saved (or patched) settings blob field by field; bad fields fall back to defaults. */
export function normalizeAudio(v: unknown): AudioSettings {
  const saved = (typeof v === 'object' && v !== null ? v : {}) as Partial<Record<string, unknown>>;
  const bool = (x: unknown, fallback: boolean) => (typeof x === 'boolean' ? x : fallback);
  return {
    master: clampVolume(saved.master, DEFAULT_AUDIO.master),
    music: clampVolume(saved.music, DEFAULT_AUDIO.music),
    sfx: clampVolume(saved.sfx, DEFAULT_AUDIO.sfx),
    muted: bool(saved.muted, DEFAULT_AUDIO.muted),
    muteInBackground: bool(saved.muteInBackground, DEFAULT_AUDIO.muteInBackground),
  };
}

/** Slider → linear gain. Squared, so the slider feels even across its range (spec §2). */
export function sliderToGain(v: number): number {
  const x = clampVolume(v, 0) / 100;
  return x * x;
}

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/** Gain of each bus node. Mute lives on master, so the bus sliders keep their values. */
export interface BusGains {
  master: number;
  music: number;
  sfx: number;
}

export function busGains(s: AudioSettings): BusGains {
  return {
    master: s.muted ? 0 : sliderToGain(s.master),
    music: sliderToGain(s.music),
    sfx: sliderToGain(s.sfx),
  };
}

/** What a sound on `bus` actually plays at: master × bus × (muted ? 0 : 1). */
export function effectiveGain(s: AudioSettings, bus: 'music' | 'sfx'): number {
  const g = busGains(s);
  return g.master * g[bus];
}
